/**
 * Used to throttle a function call. Useful when attaching callback
 * to events like window resize
 *
 * By default, throttle will execute the function as soon as you call it for the first time, and,
 * if you call it again any number of times during the wait period, as soon as that period is over.
 * If you'd like to disable the leading-edge call, pass {leading: false}, and
 * if you'd like to disable the execution on the trailing-edge, pass {trailing: false}
 *
 * Ref: http://underscorejs.org/docs/underscore.html
 *
 * @export
 * @param {Function} func
 * @param {number} wait (ms) the amount of time to wait before executing the func again
 * @param {any} options
 * @returns
 */
export function throttle(func: any, wait: number, options: any = {}) {
  let context;
  let args;
  let result;
  let timeout = null;
  let previous = 0;
  if (!options) {
      options = {};
  }

  const later = () => {
      previous =
          options.leading === false ? 0 : Date.now() || new Date().getTime();
      timeout = null;
      result = func.apply(context, args);
      if (!timeout) {
          context = args = null;
      }
  };
  return function () {
      const now = Date.now() || new Date().getTime();
      if (!previous && options.leading === false) {
          previous = now;
      }
      const remaining = wait - (now - previous);
      context = this;
      args = arguments;
      if (remaining <= 0 || remaining > wait) {
          if (timeout) {
              clearTimeout(timeout);
              timeout = null;
          }
          previous = now;
          result = func.apply(context, args);
          if (!timeout) {
              context = args = null;
          }
      } else if (!timeout && options.trailing !== false) {
          timeout = setTimeout(later, remaining);
      }
      return result;
  };
}

export function isNullOrUndefined(object: any) {
  return object === null || object === undefined;
}

export function isArray(obj: any): boolean {
  if (!Array.isArray) {
      return Object.prototype.toString.call(obj) === '[object Array]';
  } else {
      return Array.isArray(obj);
  }
}

export function isNonEmptyArray(obj: any): boolean {
  return isArray(obj) && obj.length;
}

export function isObject(obj: any): boolean {
  return obj !== null && typeof obj === 'object';
}

/**
 * Checks if the object is empty
 *
 * @export
 * @param {*} obj
 * @returns {boolean}
 */
export function isEmptyObj(obj: any): boolean {
  if (isUndefined(obj)) {
      return true;
  }

  return getObjLength(obj) === 0;
}

export function isBoolean(obj: any): boolean {
  return obj === true || obj === false || toString.call(obj) === '[object Boolean]';
}

/**
 * Removes a key from object without mutating the object
 *
 * @export
 * @param {*} key
 * @param {*} obj
 */
export function removeObjectKey(key: any, obj: any) {
  const { [key]: ignore, ...rest } = obj;
  return rest;
}

/**
 * Checks if the object has any value assigned.
 * Returns true if the object has any value including null or {}, false otherwise.
 *
 * @export
 * @param {*} obj can be any variable or object
 * @returns {boolean}
 */
export function isDefined(value: any): boolean {
  return 'undefined' !== typeof value;
}

/**
 * Checks if the object does not have any value assigned.
 * Returns false if the object does not have any value assigned
 * not even null or {}, true otherwise
 *
 * @export
 * @param {*} obj can be any variable or object
 * @returns {boolean}
 */
export function isUndefined(value: any): boolean {
  return 'undefined' === typeof value;
}

/**
 * Creates a deep clone of the object and returns it.
 * For this to work, it is important that the object is json serializable
 *
 * @param obj
 */
export function cloneObj(obj: any): any {
  if (!obj) {
      return null;
  }
  return JSON.parse(JSON.stringify(obj));
}

/**
* Returns the length of the object
*
* @export
* @param {*} obj
* @returns {number}
*/
export function getObjLength(obj: any): number {
  if (!obj) {
      return 0;
  }

  return Object.keys(obj).length;
}

export function isNumber(obj: any): boolean {
  return obj >=0 || obj < 0;
}

/**
 * Creates an instance of a class and merges the values from a source obj.
 * Only copies the values defined on the class.
 * @param obj
 * @param classRef
 * @returns {any & any}
 */
export function pickClass(obj, classRef) {
  const classObj = new classRef();
  // const keys = Object.entries(classObj).reduce((accumulator, [key, value]) => accumulator.push(key), []);
  const keys = Object.keys(classObj);
  return Object.assign(
      classObj,
      ...keys.map((k) => (k in obj ? { [k]: obj[k] } : {}))
  );
}

/**
 * Generates a GUID/UUID using the browser's crypto API if available,
 * otherwise falls back to manual generation.
 * @returns {string} A GUID in the format xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 */
export function generateGuid(): string {
    // Use the browser's crypto API for secure UUID generation
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    
    // Fallback to manual generation for browsers that don't support crypto.randomUUID
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}
